import { AsyncPipe } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Observable, of, Subject, takeUntil } from 'rxjs';
import {
  SchoolMember,
  SchoolMemberType,
} from '../../../../models/member.school.model';
import { UserModel } from '../../../../models/user.model';
import { InitialsPipe } from '../../../../pipes/initials.pipe';
import { SchoolMemberService } from '../../../../services/member.school.service';
import { UtilsService } from '../../../../services/utils.service';
import * as memberActions from '../../../../state/actions/member.school.actions';
import { AppState } from '../../../../state/appstate';
import * as groupSelector from '../../../../state/selectors/group.selector';
import * as memberSelector from '../../../../state/selectors/member.school.selector';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { BtnFabComponent } from '../../../components/buttons/btn-fab/btn-fab.component';
import { BtnMemberStatusComponent } from '../../../components/buttons/btn-member-status/btn-member-status.component';
import { AlertType } from '../../../components/dialogs/confirm.dialog.model';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { ListUtilsBarComponent } from '../../../components/list-utils-bar/list-utils-bar.component';
import { LoadingMemberComponent } from '../../../components/loaders/loading-member/loading-member.component';

@Component({
  selector: 'app-detail-parent',
  standalone: true,
  imports: [
    AsyncPipe,
    InitialsPipe,
    RouterLink,
    ListUtilsBarComponent,
    BtnBackComponent,
    BtnFabComponent,
    BtnMemberStatusComponent,
    BtnMemberStatusComponent,
    LoadingMemberComponent,
  ],
  templateUrl: './detail-parent.component.html',
  styleUrl: './detail-parent.component.css',
})
export class DetailParentComponent implements OnInit, OnDestroy {
  router: Router = inject(Router);
  toastr: ToastrService = inject(ToastrService);
  private dialog: MatDialog = inject(MatDialog);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  memberService: SchoolMemberService = inject(SchoolMemberService);
  store: Store<AppState> = inject(Store<AppState>);

  private exit$ = new Subject<void>();

  myRoles$: Observable<any> = new Observable();

  groupId: string = this.activatedRoute.snapshot.paramMap.get('id')!;
  parentId: string = this.activatedRoute.snapshot.paramMap.get('parentId')!;
  user$: Observable<UserModel | null> = new Observable();

  loading$: Observable<boolean> = new Observable();
  inProgress$: Observable<boolean> = new Observable();
  isSearching: Observable<boolean> = of(false);

  children: SchoolMember[] = [];
  parent: SchoolMember | null | undefined = null;

  utilsService = UtilsService;

  constructor() {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id')!;
        this.parentId = params.get('parentId')!;
      });

    this.user$ = this.store.select((state) => state.user.user);

    this.loading$ = this.store.select(memberSelector.loading);
    this.inProgress$ = this.store.select(memberSelector.inProgress);

    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.store
      .select(memberSelector.getSchoolMemberById(this.parentId, this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((parent) => {
        this.parent = parent;
      });

    this.getChildren();
  }

  ngOnInit(): void {
    if (!this.parent) {
      if (this.groupId && this.parentId)
        this.store.dispatch(
          memberActions.getMember({
            groupId: this.groupId,
            memberId: this.parentId,
          }),
        );
    } else if (this.parent.studentId && this.children?.length === 0) {
      this.loadChildren();
    }
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  loadChildren(): void {
    this.parent?.studentId?.map((childId) => {
      this.store.dispatch(
        memberActions.getMember({
          groupId: this.groupId,
          memberId: childId,
        }),
      );
    });
  }

  getChildren() {
    this.store
      .select(memberSelector.getStudentsByParentId(this.parentId, this.groupId))
      .pipe(takeUntil(this.exit$))
      .subscribe((children) => {
        this.children = children;
      });
  }

  onSearchTermChange(value: string) {
    this.store
      .select(memberSelector.getStudentsByParentId(this.parentId, value))
      .pipe(takeUntil(this.exit$))
      .subscribe((children) => {
        this.children = children;
      });
  }

  async onSearch(term: string) {
    console.log(`Searching for: ${term}`);
    this.isSearching = of(true);
    const result = await this.memberService.searchMembers(term, this.groupId!, {
      memberType: [SchoolMemberType.student],
    });
    console.log(`Search Result records: ${result.length}`);
    this.children = result;
    this.isSearching = of(false);
  }

  onRefresh(): void {
    this.loadChildren();
  }

  async onDelinkChild(child: SchoolMember) {
    const result = await this.onShowAlert(
      `<span>Are you sure you want to delink <b>${child.name}</b>?</span>`,
    );
    if (!result) return;

    try {
      // console.log(
      //   `Selected Parent: ${JSON.stringify(this.selectedParent, null, 2)}`
      // );

      let student: SchoolMember = {
        id: child.id,
        groupId: child!.groupId,
        memberId: child!.memberId,
        academicYear: child.gradeYear ?? this.parent?.academicYear,
        modified: Timestamp.now(),
      };
      // console.log(`Selected Student: ${JSON.stringify(student, null, 2)}`);

      let parent: SchoolMember = {
        id: this.parent?.id,
        groupId: this.parent!.groupId,
        memberId: this.parent!.memberId,
        academicYear: child.gradeYear ?? this.parent?.academicYear,
        studentId: this.parent?.studentId?.filter(
          (id) => id !== child.memberId,
        ),
        modified: Timestamp.now(),
      };

      this.store.dispatch(
        memberActions.delinkChild({
          student: student,
          parent: parent,
        }),
      );
      // console.log(`submit : Action dispatched`);
    } catch (error) {
      // console.log(`DlgLinkParentComponent :: submit : Error: `, error);
      this.toastr.error('Failed to delink Child', 'Error Occurred');
    }
  }

  async onShowAlert(description: String): Promise<boolean> {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: 'Delink Child',
      description: description,
      action: 'Delink',
      buttonColor: 'bg-primary-500',
      showType: AlertType.warning,
    };

    const dialogRef = this.dialog.open(DlgConfirmComponent, dialogConfig);

    const result = await firstValueFrom(dialogRef.afterClosed());

    return !!result;
  }

  // TODO Query params should send the studentId.
  // Only principal, president and the studentId == uid, should be allowed to view this page.
}
